import { decodeValue, deparam, getBaseUrl } from '@/lib/utils';
import map from 'lodash/map';
import MutationObserver from '@/lib/mutationObserver';
import ResizeObserver from 'resize-observer-polyfill';
import { REVIEW_FILTER_PARAMS } from '@/constants/PARAMS';

class IframeWindowService {
  constructor(router) {
    this.router = router;
    const { iframe_id, parent_url } = deparam(
      window.location.href.split('?')[1]
    );
    this.iframeId = iframe_id || '';
    this.parentUrl = parent_url ? decodeURIComponent(parent_url) : null;
    this.isMessageSending = false;
    this.scrollTop = null;
    this.windowHeight = null;
    this.messageHandlers = [];

    if (this.parentUrl) {
      const sourceOrigin = new URL(this.parentUrl).origin;
      window.addEventListener('message', e => {
        if (typeof sourceOrigin === 'string' && e.origin !== sourceOrigin)
          return;

        let data;
        if (e.ports && e.ports[0]) {
          this.port2 = e.ports[0];
          data = e.data;
        } else if (typeof e.data === 'object') data = e.data;
        else data = deparam(e.data);

        this.handleMessage(data);
      });
    }
  }

  alert(msg) {
    this.postMessage('alert', { msg });
  }

  backToReferrer() {
    // Do nothing
  }

  share(shareData) {
    this.postMessage('share', shareData);
  }

  confirm(msg) {
    if (this.isMessageSending) return;

    const channel = new MessageChannel();
    window.parent.postMessage('confirm_message', getBaseUrl(this.parentUrl), [
      channel.port2
    ]);
    let outsideResolve;
    const promise = new Promise(function(resolve) {
      outsideResolve = resolve;
    });
    channel.port1.onmessage = e => {
      this.isMessageSending = false;
      outsideResolve(e.data);
    };
    this.isMessageSending = true;
    channel.port1.postMessage(msg);

    return promise;
  }

  getScrollTop() {
    return this.scrollTop || window.scrollY;
  }

  getWindowHeight() {
    return this.windowHeight || window.innerHeight;
  }

  scrollTo(offset) {
    this.postMessage('scroll_to', { offset });
  }

  addMessageHandler(messageHandler) {
    this.messageHandlers.push(messageHandler);
  }

  removeMessageHandler(messageHandler) {
    this.messageHandlers = this.messageHandlers.filter(
      e => e !== messageHandler
    );
  }

  openFullscreenPopup(url, targetElement) {
    return new Promise(resolve => {
      const messageHandler = data => {
        if (
          data.act === 'fullscreen_popup' &&
          data.fullscreen_popup_act === 'closed'
        ) {
          targetElement?.focus();
          this.removeMessageHandler(messageHandler);
          resolve(this.decodeFormValue(data.response));
        }
      };
      this.addMessageHandler(messageHandler);
      this.postMessage('open_fullscreen_popup', {
        url: encodeURIComponent(url)
      });
    });
  }

  closeFullscreenPopup(response = '') {
    this.postMessage('close_fullscreen_popup', {
      response: encodeURIComponent(response)
    });
  }

  loginUrl(url) {
    return url;
  }

  redirectTo(url, args = {}) {
    this.postMessage('redirect_to', { ...args, url: encodeURIComponent(url) });
  }

  setBodyMinHeight(height) {
    document.body.style.minHeight = `${height}px`;
  }

  clearBodyMinHeight() {
    document.body.style.minHeight = '';
  }

  setParentDivMargin(style) {
    if (!style || this.isParentDivMarginSet) return;

    this.isParentDivMarginSet = true;
    this.postMessage('set_parent_div_margin', { style });
  }

  setParentDivWidth(style) {
    if (!style || this.isParentDivWidthSet) return;

    this.isParentDivWidthSet = true;
    this.postMessage('set_parent_div_width', {
      style: encodeURIComponent(style)
    });
  }

  sendReady(iframe_id) {
    this.postMessage('ready', { iframe_id: iframe_id });
  }

  sendHide(iframe_id) {
    this.postMessage('hide', { iframe_id: iframe_id });
  }

  pushState(query) {
    this.router.replace({ query });
    this.postMessage('paginate', {
      ...this.paginateParams(query),
      push_state: true
    });
  }

  paginateParams(params) {
    return Object.keys(params)
      .filter(k => {
        return (
          REVIEW_FILTER_PARAMS.includes(k) ||
          k.startsWith('option_') ||
          k.startsWith('product_option_')
        );
      })
      .reduce((a, k) => ({ ...a, [k]: params[k] }), {});
  }

  postMessage(act, args) {
    const queryString = map(
      { iframe_id: this.iframeId, act, ...args },
      (v, k) => `${k}=${v}`
    ).join('&');
    window.parent.postMessage(queryString, this.parentUrl);
  }

  handleMessage(data) {
    switch (data.act) {
      case 'scroll':
        this.scrollTop = Number(data.scrollTop);
        window.dispatchEvent(new CustomEvent('scroll'));
        break;
      case 'set_window_height':
        this.windowHeight = Number(data.window_height);
        break;
      case 'popstate':
        this.onPopstate(data.url);
        break;
      default:
        this.messageHandlers.forEach(f => f(data));
        break;
    }
  }

  decodeFormValue(value) {
    if (value == null) return value;
    return decodeValue(value.replace(/\+/g, ' '));
  }

  onPopstate(url) {
    const getParamsWithoutPrefix = (url, prefix) => {
      const paramsWithPrefix = deparam(
        decodeURIComponent(url).split('?')[1] || ''
      );
      const paramsWithoutPrefix = {};
      for (const [k, v] of Object.entries(paramsWithPrefix)) {
        const key = k.startsWith(prefix) ? k.substring(prefix.length) : k;
        paramsWithoutPrefix[key] = decodeValue(v);
      }
      return paramsWithoutPrefix;
    };

    this.messageHandlers.forEach(f =>
      f({
        act: 'popstate',
        params: getParamsWithoutPrefix(url, `${this.iframeId}-`)
      })
    );
  }

  initObservers() {
    const isFullscreenPopup = this.iframeId.indexOf('fullscreen-popup') !== -1;
    if (isFullscreenPopup) return;

    let currentHeight = document.body.offsetHeight;
    const resetHeight = () => {
      const watermarkHeight = document.getElementsByClassName('watermark')[0]
        ?.offsetHeight;
      const height = Math.max(document.body.offsetHeight, watermarkHeight);
      if (currentHeight === height) return;

      currentHeight = height;
      this.postMessage('set_height', { height });
    };

    this.mutationObserver = new MutationObserver(mutationsList => {
      if (mutationsList.length) resetHeight();
    });
    this.mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    });

    this.resizeObserver = new ResizeObserver(() => resetHeight());
    this.resizeObserver.observe(document.body);
  }
}

export default IframeWindowService;
